import { MutationFunctionOptions } from '@apollo/react-common';
import { Action } from '../../Card';
import { Content, Footer, Header, TripleAction } from '../../../libs/gs-component-right-rail';
import { InlineAlert } from '@xo-union/tk-component-alerts';
import { ProgressBar } from '@xo-union/tk-component-progress-bar';
import Honeybadger from 'honeybadger-js';
import pluralize from 'pluralize';
import React from 'react';

import { PhotoUploading } from '.';
import {
  PhotoGalleryItemsFragment,
  UpdatePhotoGalleryItemMutation,
  UpdatePhotoGalleryItemMutationVariables,
  useCreatePhotoGalleryItemMutation,
  Wws_PageItem,
} from '../../../generated/graphql';
import addItemToPage from '../../../helpers/apolloCache/addItemToPage';
import updatePageItem from '../../../helpers/apolloCache/updatePageItem';
import { useManageItem } from '../../../helpers/useManageItem';
import styles from './upload.scss';
import { pagesQuery } from '../../LeftContainer/graphql';

pluralize.addIrregularRule('it', 'them');

interface PhotoGalleryUploadProps {
  addUploadsOnChange(event: React.ChangeEvent<HTMLInputElement>): void;
  id: number | null;
  pageId: number;
  photosUploading: PhotoUploading[];
  setPhotoItems: Function;
  setPhotosUploading: Function;
  update(
    options?: MutationFunctionOptions<UpdatePhotoGalleryItemMutation, UpdatePhotoGalleryItemMutationVariables>
  ): Promise<any>;
  updateLoading: boolean;
  uploadsWithErrors: PhotoUploading[];
  trackItem(pageItem?: Wws_PageItem): void;
}

const PhotoGalleryUpload = (props: PhotoGalleryUploadProps) => {
  const [create, { loading: createLoading }] = useCreatePhotoGalleryItemMutation();
  const { close } = useManageItem();

  const {
    addUploadsOnChange,
    id,
    pageId,
    photosUploading,
    setPhotoItems,
    setPhotosUploading,
    update,
    updateLoading,
    uploadsWithErrors,
    trackItem,
  } = props;

  const photosUploaded = photosUploading.filter(
    photoUploading => photoUploading.mediaResponse && !photoUploading.error
  );

  const isUploading = !!photosUploading.filter(
    upload => upload.percentUploaded && upload.percentUploaded < 100 && !upload.error
  ).length;
  const isSaving = createLoading || updateLoading;
  const isSavingOrNoPhotosUploaded = isUploading || isSaving || photosUploaded.length === 0;
  const uploadsErrorCount = uploadsWithErrors.length;

  const clearUploads = () => {
    setPhotosUploading([]);
  };

  const saveUploads = async () => {
    const newPhotoItems = photosUploaded.map(({ mediaResponse }) => ({
      pageId,
      mediaApiId: mediaResponse?.mediaApiId || '',
      height: mediaResponse?.height || 0,
      width: mediaResponse?.width || 0,
    }));

    try {
      if (id) {
        await update({
          variables: {
            attributes: {
              pageId,
              photoItems: newPhotoItems,
              id,
            },
          },
          update(cache, { data }) {
            const item = data?.item;
            if (item) {
              setPhotoItems(item.photoItems);
              updatePageItem<PhotoGalleryItemsFragment | undefined>(cache, pageId, id, item);
              trackItem(item);
              clearUploads();
              close(item);
            }
          },
        });
      } else {
        await create({
          variables: {
            attributes: {
              pageId,
              photoItems: newPhotoItems,
            },
          },
          update(cache, { data }) {
            const item = data?.item;
            if (item) {
              addItemToPage<PhotoGalleryItemsFragment>(cache, pageId, item);
              trackItem(item);
              clearUploads();
              close(item);
            }
          },
          refetchQueries: [{ query: pagesQuery }],
        });
      }
    } catch (error) {
      Honeybadger.notify(error, 'Error Saving Uploaded Photos');
    }
  };

  const clearUploadsIfNotSaving = () => {
    if (!isSaving) clearUploads();
  };

  return (
    <>
      <Header leftButtonText=" " leftButtonOnClick={clearUploadsIfNotSaving} as="h1">
        Add Photos to Gallery
      </Header>
      <Content>
        <label className={styles.addMorePhotos}>
          <Action iconName="add_circle" onClick={() => {}} data-testid="add-more-photos">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={addUploadsOnChange}
              style={{ display: 'none' }}
              data-testid="photo-gallery-add-more"
            />
            Add More Photos
          </Action>
        </label>
        {uploadsErrorCount > 0 && (
          <InlineAlert type="info" className={styles.uploadErrorBanner}>
            {`Oops! It looks like ${uploadsErrorCount} ${pluralize('photo', uploadsErrorCount)} didn't upload. `}
            <strong>{`Please try ${pluralize('it', uploadsErrorCount)} again.`}</strong>
          </InlineAlert>
        )}
        {photosUploading.map(({ dataUrl, error, key, percentUploaded }, index) => (
          <div className={styles.uploadRow} key={key}>
            <div className={styles.imageSquare}>
              <img alt={`Gallery Item ${index + 1}`} src={dataUrl} className={styles.image} />
            </div>
            <div className={styles.uploadStatus}>
              {!error ? (
                <ProgressBar progress={percentUploaded / 100} color="green" />
              ) : (
                <span className={styles.uploadError}>{error.message}</span>
              )}
            </div>
          </div>
        ))}
      </Content>
      <Footer>
        <TripleAction
          secondaryText="Close"
          secondaryOnClick={clearUploads}
          secondaryDisabled={isSaving}
          primaryText="Add to Gallery"
          primaryOnClick={saveUploads}
          primaryDisabled={isSavingOrNoPhotosUploaded}
        />
      </Footer>
    </>
  );
};

export default PhotoGalleryUpload;
